import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { McpAccessScope } from '../../../Api/McpAccess';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';

export interface McpAccessCreateFormValues {
  title: string;
  scope: McpAccessScope;
}

export interface McpAccessCreateFormProps {
  submitting: boolean;
  onSubmit: (values: McpAccessCreateFormValues) => void;
}

const DEFAULT_VALUES: McpAccessCreateFormValues = {
  title: '',
  scope: 'read_only'
};

export const McpAccessCreateForm = ({ submitting, onSubmit }: McpAccessCreateFormProps) => {
  const { control, handleSubmit, register, formState, reset } = useForm<McpAccessCreateFormValues>({
    defaultValues: DEFAULT_VALUES
  });
  const { errors } = formState;

  const handleFormSubmit = (values: McpAccessCreateFormValues) => {
    onSubmit({ ...values, title: values.title.trim() });
    reset(DEFAULT_VALUES);
  };

  return (
    <Box
      component="form"
      data-testid="mcp-access-create-form"
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480, mb: 3 }}>
      <TextField
        label="Title"
        size="small"
        required
        disabled={submitting}
        error={!!errors.title}
        helperText={errors.title?.message}
        inputProps={{ 'data-testid': 'mcp-access-title-input', 'aria-label': 'Title' }}
        {...register('title', {
          required: 'Title is required',
          validate: (value) => value.trim().length > 0 || 'Title is required'
        })}
      />
      <FormControl
        component="fieldset"
        disabled={submitting}>
        <FormLabel component="legend">Scope</FormLabel>
        <Controller
          control={control}
          name="scope"
          render={({ field }) => (
            <RadioGroup
              row
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}>
              <FormControlLabel
                value="read_only"
                control={<Radio data-testid="scope-read-only" />}
                label="Read only"
              />
              <FormControlLabel
                value="read_write"
                control={<Radio data-testid="scope-read-write" />}
                label="Read & write"
              />
            </RadioGroup>
          )}
        />
      </FormControl>
      <Box>
        <button
          type="submit"
          disabled={submitting}
          data-testid="mcp-access-submit"
          className={`${DEFAULT_ADVANCE_BUTTON_STYLE} inline-flex items-center gap-2`}>
          {submitting && (
            <CircularProgress
              size={16}
              color="inherit"
            />
          )}
          {submitting ? 'Generating…' : 'Generate token'}
        </button>
      </Box>
    </Box>
  );
};
